import React from "react";
import PropTypes from "prop-types";
import { Modal, Input } from "antd";

const { TextArea } = Input;

// Turns a schema.columns array into an initial `row` shape (each key ->
// the passed initial value or empty string). Used when adding a fresh row.
function emptyRow(columns) {
    return (columns || []).reduce((acc, col) => {
        acc[col.key] = "";
        return acc;
    }, {});
}

/**
 * Add / edit modal for a single policy row. The parent decides whether it's
 * an add (initialRow blank) or edit (initialRow prefilled); both flows are the
 * same form component.
 *
 * The submit handler receives the new row object; persistence is the parent's
 * job (it will PUT the whole policy content, since the backend replaces the
 * full rows array on every save).
 */
class PolicyRowEditor extends React.Component {
    state = { row: {} };

    componentDidMount() {
        this.hydrate();
    }

    componentDidUpdate(prev) {
        // Re-hydrate on re-open so pre-filled row doesn't get retained from
        // a previous edit (parent toggles `visible` between opens).
        if (prev.visible !== this.props.visible && this.props.visible) {
            this.hydrate();
        }
    }

    hydrate() {
        const { initialRow, columns } = this.props;
        const base = emptyRow(columns);
        this.setState({ row: { ...base, ...(initialRow || {}) } });
    }

    setField = (key, value) =>
        this.setState(prev => ({ row: { ...prev.row, [key]: value } }));

    handleOk = () => {
        if (this.props.onSubmit) {
            this.props.onSubmit(this.state.row);
        }
    };

    renderField(col) {
        const value = this.state.row[col.key] || "";
        if (col.type === "textarea") {
            return (
                <TextArea
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    value={value}
                    onChange={e => this.setField(col.key, e.target.value)}
                />
            );
        }
        return (
            <Input
                value={value}
                onChange={e => this.setField(col.key, e.target.value)}
            />
        );
    }

    render() {
        const { visible, onCancel, columns, mode } = this.props;
        return (
            <Modal
                title={mode === "add" ? "Add row" : "Edit row"}
                visible={visible}
                onOk={this.handleOk}
                onCancel={onCancel}
                okText={"Save"}
                destroyOnClose
                width={640}
            >
                <div className={"policy-hub-form"}>
                    {(columns || []).map(col => (
                        <div key={col.key} className={"policy-hub-form__field"}>
                            <span className={"policy-hub-form__label"}>
                                {col.label || col.key}
                            </span>
                            {this.renderField(col)}
                        </div>
                    ))}
                </div>
            </Modal>
        );
    }
}

PolicyRowEditor.propTypes = {
    visible: PropTypes.bool,
    mode: PropTypes.oneOf(["add", "edit"]),
    columns: PropTypes.array,
    initialRow: PropTypes.object,
    onSubmit: PropTypes.func,
    onCancel: PropTypes.func,
};

export default PolicyRowEditor;
